import React, { useState } from "react";
import {
  Paper,
  Box,
  Typography,
  Switch,
  Button,
  Divider,
  Grid,
  FormControlLabel,
  Checkbox,
  MenuItem,
  TextField,
} from "@mui/material";
import PetsIcon from "@mui/icons-material/Pets";

export interface PetPreferencesData {
  dogs: {
    sizes: string[];
    ages: string[];
    maxNumber: number;
  };
  cats: {
    accepted: boolean;
    maxNumber: number;
  };
  otherPets: string[];
  requirements: {
    houseTrained: boolean;
    vaccinated: boolean;
    spayedNeutered: boolean;
  };
}

interface PetPreferencesSectionProps {
  data: PetPreferencesData;
  onSave: (data: PetPreferencesData) => void;
}

const switchSx = {
  "& .MuiSwitch-switchBase.Mui-checked": { color: "#178B8E" },
  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { backgroundColor: "#178B8E" },
};

const checkboxSx = {
  color: "#178B8E",
  "&.Mui-checked": { color: "#178B8E" },
};

const DOG_SIZES = ["small", "medium", "large", "giant"];
const DOG_AGES = ["puppy", "adult", "senior"];
const OTHER_PETS = ["birds", "rabbits", "hamsters", "reptiles", "fish", "guinea pigs"];
const MAX_OPTIONS = [1, 2, 3, 4, 5];

const PetPreferencesSection: React.FC<PetPreferencesSectionProps> = ({ data, onSave }) => {
  const [draft, setDraft] = useState<PetPreferencesData>(data);

  const toggleArray = (arr: string[], value: string) =>
    arr.includes(value) ? arr.filter((v) => v !== value) : [...arr, value];

  const handleDogSize = (size: string) => {
    setDraft((prev) => ({ ...prev, dogs: { ...prev.dogs, sizes: toggleArray(prev.dogs.sizes, size) } }));
  };

  const handleDogAge = (age: string) => {
    setDraft((prev) => ({ ...prev, dogs: { ...prev.dogs, ages: toggleArray(prev.dogs.ages, age) } }));
  };

  const handleDogMax = (value: string) => {
    setDraft((prev) => ({ ...prev, dogs: { ...prev.dogs, maxNumber: parseInt(value) } }));
  };

  const handleCatAccepted = () => {
    setDraft((prev) => ({ ...prev, cats: { ...prev.cats, accepted: !prev.cats.accepted } }));
  };

  const handleCatMax = (value: string) => {
    setDraft((prev) => ({ ...prev, cats: { ...prev.cats, maxNumber: parseInt(value) } }));
  };

  const handleOtherPet = (pet: string) => {
    setDraft((prev) => ({ ...prev, otherPets: toggleArray(prev.otherPets, pet) }));
  };

  const handleRequirement = (field: keyof PetPreferencesData["requirements"]) => {
    setDraft((prev) => ({
      ...prev,
      requirements: { ...prev.requirements, [field]: !prev.requirements[field] },
    }));
  };

  return (
    <Paper sx={{ p: 3, borderRadius: 2, mb: 3 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
        <PetsIcon sx={{ color: "#178B8E", fontSize: 22 }} />
        <Typography variant="h6" fontWeight={600}>
          Pet Preferences
        </Typography>
      </Box>
      <Typography variant="body2" color="text.secondary" mb={3}>
        Specify which pets you're comfortable caring for
      </Typography>

      {/* Dogs */}
      <Typography variant="body1" fontWeight={600} mb={1.5}>
        Dogs
      </Typography>
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid size={{ xs: 12, sm: 6 }}>
          <Typography variant="body2" color="text.secondary" mb={1}>
            Accepted sizes
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
            {DOG_SIZES.map((size) => (
              <FormControlLabel
                key={size}
                control={
                  <Checkbox
                    checked={draft.dogs.sizes.includes(size)}
                    onChange={() => handleDogSize(size)}
                    size="small"
                    sx={checkboxSx}
                  />
                }
                label={<Typography variant="body2" sx={{ textTransform: "capitalize" }}>{size}</Typography>}
                sx={{ mr: 0, minWidth: 100 }}
              />
            ))}
          </Box>
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <Typography variant="body2" color="text.secondary" mb={1}>
            Accepted ages
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
            {DOG_AGES.map((age) => (
              <FormControlLabel
                key={age}
                control={
                  <Checkbox
                    checked={draft.dogs.ages.includes(age)}
                    onChange={() => handleDogAge(age)}
                    size="small"
                    sx={checkboxSx}
                  />
                }
                label={<Typography variant="body2" sx={{ textTransform: "capitalize" }}>{age}</Typography>}
                sx={{ mr: 0, minWidth: 100 }}
              />
            ))}
          </Box>
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <TextField
            select
            fullWidth
            label="Max dogs at once"
            value={draft.dogs.maxNumber}
            onChange={(e) => handleDogMax(e.target.value)}
            size="small"
          >
            {MAX_OPTIONS.map((n) => (
              <MenuItem key={n} value={n}>{n === 5 ? "5+" : n}</MenuItem>
            ))}
          </TextField>
        </Grid>
      </Grid>

      <Divider sx={{ my: 2.5 }} />

      {/* Cats */}
      <Typography variant="body1" fontWeight={600} mb={1.5}>
        Cats
      </Typography>
      <Grid container spacing={2} alignItems="center" sx={{ mb: 0.5 }}>
        <Grid size={{ xs: 12, sm: 6 }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <Typography variant="body2">Accept cats</Typography>
            <Switch
              checked={draft.cats.accepted}
              onChange={handleCatAccepted}
              size="small"
              sx={switchSx}
            />
          </Box>
        </Grid>
        {draft.cats.accepted && (
          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField
              select
              fullWidth
              label="Max cats at once"
              value={draft.cats.maxNumber}
              onChange={(e) => handleCatMax(e.target.value)}
              size="small"
            >
              {MAX_OPTIONS.map((n) => (
                <MenuItem key={n} value={n}>{n === 5 ? "5+" : n}</MenuItem>
              ))}
            </TextField>
          </Grid>
        )}
      </Grid>

      <Divider sx={{ my: 2.5 }} />

      {/* Other Pets */}
      <Typography variant="body1" fontWeight={600} mb={1}>
        Other Pets
      </Typography>
      <Box sx={{ display: "flex", flexWrap: "wrap" }}>
        {OTHER_PETS.map((pet) => (
          <FormControlLabel
            key={pet}
            control={
              <Checkbox
                checked={draft.otherPets.includes(pet)}
                onChange={() => handleOtherPet(pet)}
                size="small"
                sx={checkboxSx}
              />
            }
            label={
              <Typography variant="body2" sx={{ textTransform: "capitalize" }}>{pet}</Typography>
            }
            sx={{ minWidth: 150 }}
          />
        ))}
      </Box>

      <Divider sx={{ my: 2.5 }} />

      {/* Requirements */}
      <Typography variant="body1" fontWeight={600} mb={1.5}>
        Pet Requirements
      </Typography>
      <Grid container spacing={1}>
        {(
          [
            { key: "houseTrained", label: "Must be house trained" },
            { key: "vaccinated", label: "Must be vaccinated" },
            { key: "spayedNeutered", label: "Must be spayed / neutered" },
          ] as { key: keyof PetPreferencesData["requirements"]; label: string }[]
        ).map(({ key, label }) => (
          <Grid size={{ xs: 12, sm: 6 }} key={key}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", pr: 2 }}>
              <Typography variant="body2">{label}</Typography>
              <Switch
                checked={draft.requirements[key]}
                onChange={() => handleRequirement(key)}
                size="small"
                sx={switchSx}
              />
            </Box>
          </Grid>
        ))}
      </Grid>

      <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}>
        <Button
          variant="contained"
          size="small"
          onClick={() => onSave(draft)}
          sx={{ textTransform: "none", backgroundColor: "#178B8E", "&:hover": { backgroundColor: "#146f72" } }}
        >
          Save Changes
        </Button>
      </Box>
    </Paper>
  );
};

export default PetPreferencesSection;
